import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PLink from "@/components/PLink";
import { prefetchWhenIdle } from "@/lib/prefetch";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { RefreshCw, Plus, Briefcase, HardHat, Users } from "lucide-react";
import { LogoutButton } from "@/components/LogoutButton";
import { MobileMenu } from "@/components/MobileMenu";
import { useAutoPush } from "@/hooks/useAutoPush";
import Avatar from "@/components/Avatar";
import { useAttention, CountBadge } from "@/hooks/useAttention";

function LoopPhoneMark({ size = 28, className = "" }) {
  return (
    <img
      src="/tradieloop-logo.webp"
      alt="TradieLoop"
      className={`block object-contain object-left ${className}`}
      style={{ height: size, width: "auto" }}
      data-testid="brand-mark"
    />
  );
}

function usePendingCount(isAdmin, pathname) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) { setCount(0); return; }
    let alive = true;
    const fetchCount = () => api.get("/admin/setup/pending-count").then((r) => alive && setCount(r.data?.count || 0)).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [isAdmin, pathname]);
  return count;
}

function AdminLabel({ count }) {
  return (
    <span className="inline-flex items-center gap-1.5">Admin
      {count > 0 && <span className="min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#FF6A00] text-black text-[10px] font-black leading-none" data-testid="nav-pending-approvals-badge">{count}</span>}
    </span>
  );
}

export default function Nav() {
  const { user } = useAuth();
  const loc = useLocation();
  const pending = usePendingCount(!!user?.is_admin, loc.pathname);
  const attn = useAttention(!!user);
  useAutoPush();
  useEffect(() => {
    if (!user) { prefetchWhenIdle(["/login", "/register", "/jobs/:id", "/contractors/:id"]); return; }
    prefetchWhenIdle(["/dashboard", "/bookings", "/profile", "/jobs/:id", "/contractors/:id", user.role === "builder" ? "/jobs/new" : "/availability/new", ...(user.is_admin ? ["/admin"] : [])]);
  }, [user]);

  const linkCls = (path) =>
    `px-2 xl:px-3 py-2 text-[11px] xl:text-xs uppercase tracking-wider xl:tracking-widest font-semibold whitespace-nowrap transition-colors duration-150 ${
      loc.pathname === path ? "text-[#FF6A00]" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <nav className="dark-zone sticky top-0 z-50 bg-[#0A0A0A] text-white shadow-[0_5px_0_0_#FF6A00,0_12px_30px_-18px_rgba(0,0,0,0.6)]" data-testid="site-nav">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between gap-4 h-14">
        <PLink to={user ? "/dashboard" : "/"} className="flex items-center flex-shrink min-w-0" data-testid="brand-home-link">
          <LoopPhoneMark size={40} className="max-w-[46vw] sm:max-w-none" />
        </PLink>

        <div className="hidden xl:flex items-center flex-1 justify-center gap-0 2xl:gap-1 min-w-0">
          {user && <PLink to="/dashboard" className={linkCls("/dashboard")} data-testid="nav-dashboard">Home</PLink>}
          <PLink to="/jobs" className={linkCls("/jobs")} data-testid="nav-jobs">{user?.role === "builder" ? "Job feed" : "Find work"}</PLink>
          <PLink to="/contractors" className={linkCls("/contractors")} data-testid="nav-contractors">Tradies</PLink>
          {user?.role === "contractor" && <PLink to="/rate-guide" className={linkCls("/rate-guide")} data-testid="nav-rate-guide">Rate guide</PLink>}
          {user && <PLink to="/bookings" className={`${linkCls("/bookings")} inline-flex items-center gap-1.5`} data-testid="nav-bookings">{user.role === "builder" ? "My jobs" : "Bookings"}<CountBadge n={attn.bookings} testid="nav-bookings-badge" /></PLink>}
          {user && <PLink to="/profile" className={linkCls("/profile")} data-testid="nav-profile">Profile</PLink>}
          <PLink to="/how-it-works" className={linkCls("/how-it-works")} data-testid="nav-how">How it works</PLink>
          {user?.is_admin && <PLink to="/admin" className={linkCls("/admin")} data-testid="nav-admin"><AdminLabel count={pending} /></PLink>}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {user ? (
            <>
              {user.role === "builder" && (
                <PLink to="/jobs/new" className="hidden sm:inline-flex items-center gap-1 px-3 py-2 bg-[#FF6A00] text-black text-xs font-black uppercase tracking-widest whitespace-nowrap hover:bg-white" data-testid="nav-cta-post-job">
                  <Plus className="w-3.5 h-3.5" /> Post job
                </PLink>
              )}
              {user.role === "contractor" && (
                <PLink to="/availability/new" className="hidden sm:inline-flex items-center gap-1 px-3 py-2 bg-[#FF6A00] text-black text-xs font-black uppercase tracking-widest whitespace-nowrap hover:bg-white" data-testid="nav-cta-avail">
                  <HardHat className="w-3.5 h-3.5" /> I'm free
                </PLink>
              )}
              <PLink to="/profile" className="hidden xl:block" title={user.email} data-testid="nav-avatar-link">
                <Avatar src={user.photo_url} name={user.name} size={28} testid="nav-avatar" />
              </PLink>
              <LogoutButton />
            </>
          ) : (
            <>
              <PLink to="/login" className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-white hover:text-[#FF6A00] whitespace-nowrap" data-testid="nav-login">Sign in</PLink>
              <PLink to="/register" className="px-4 py-2 bg-[#FF6A00] text-black text-xs uppercase tracking-widest font-bold hover:bg-[#E55E00] transition-colors" data-testid="nav-register">Join</PLink>
            </>
          )}
        </div>
      </div>
      {!user && (
        <MobileMenu links={[
          { to: "/jobs", label: "Browse jobs", testid: "mnav-public-jobs" },
          { to: "/contractors", label: "Browse tradies", testid: "mnav-public-tradies" },
          { to: "/how-it-works", label: "How it works", testid: "mnav-public-how" },
        ]} cta={{ to: "/register", label: "Join TradieLoop", testid: "mnav-public-join" }} />
      )}
      {user && (
        <MobileMenu
          links={user.role === "builder" ? [
            { to: "/dashboard", label: "Home", testid: "mnav-dashboard" },
            { to: "/jobs/new", label: "Post job", testid: "mnav-post-job" },
            { to: "/contractors", label: "Tradies", testid: "mnav-contractors" },
            { to: "/bookings", label: attn.bookings ? `My jobs (${attn.bookings})` : "My jobs", testid: "mnav-bookings" },
            { to: "/jobs", label: "Job feed", testid: "mnav-jobs" },
            { to: "/profile", label: "Profile", testid: "mnav-profile" },
            { to: "/settings", label: "Settings", testid: "mnav-settings" },
            { to: "/how-it-works", label: "How it works", testid: "mnav-how" },
            ...(user.is_admin ? [{ to: "/admin", label: pending ? `Admin (${pending})` : "Admin", testid: "mnav-admin" }] : []),
          ] : [
            { to: "/dashboard", label: "Home", testid: "mnav-dashboard" },
            { to: "/availability/new", label: "I'm free", testid: "mnav-avail" },
            { to: "/rate-guide", label: "How much to charge", testid: "mnav-rate-guide" },
            { to: "/jobs", label: "Find work", testid: "mnav-jobs" },
            { to: "/contractors", label: "Tradies", testid: "mnav-contractors" },
            { to: "/bookings", label: attn.bookings ? `Bookings (${attn.bookings})` : "Bookings", testid: "mnav-bookings" },
            { to: "/profile", label: "Profile", testid: "mnav-profile" },
            { to: "/settings", label: "Settings", testid: "mnav-settings" },
            { to: "/how-it-works", label: "How it works", testid: "mnav-how" },
            ...(user.is_admin ? [{ to: "/admin", label: pending ? `Admin (${pending})` : "Admin", testid: "mnav-admin" }] : []),
          ]}
          cta={user.role === "builder" ? { to: "/jobs/new", label: "+ Post a job", testid: "mnav-cta-post-job" } : { to: "/availability/new", label: "I'm free — list availability", testid: "mnav-cta-avail" }}
        />
      )}
    </nav>
  );
}

export { LoopPhoneMark };
